import React, { useEffect, useMemo, useReducer } from 'react';
import { StepList, type Step, type StepStatus } from '@aibuild/ui';
import {
  LogCard,
  LogHead,
  LogSep,
  LogSepLabel,
  LogStatusDot,
  LogTitleRow,
  LogTitleText,
  MetaGrid,
  MetaKey,
  MetaMono,
  MetaRow,
  MetaValue,
  StepsScroll,
} from './ExecutionLog.styled';

export interface ExecutionStep {
  id: string;
  title: string;
  /** Per-node status from the run engine. Drives the step indicator. */
  nodeStatus?:
    | 'pending'
    | 'provisioning'
    | 'running'
    | 'waiting_for_runner'
    | 'waiting_for_user'
    | 'completed'
    | 'failed'
    | 'skipped';
}

export type RunPhase = 'idle' | 'running' | 'done';

/**
 * Terminal outcome of a finished run. Drives the status dot colour and
 * per-step rendering when `phase === 'done'`. Defaults to `'completed'`
 * so existing callers (test runs that always succeed) work unchanged.
 */
export type RunOutcome = 'completed' | 'failed' | 'cancelled';

export interface ExecutionLogProps {
  phase: RunPhase;
  total: number;
  order: ExecutionStep[];
  currentId: string | null;
  startedAt: number | null;
  /**
   * When the run completed (epoch ms). Required for `phase === 'done'` so the
   * displayed elapsed time freezes at the actual run duration; without it a
   * completed-run card would keep ticking up as the page lives on, showing
   * "run age" instead of "run duration".
   */
  finishedAt?: number | null;
  /**
   * Per-process sequential run number. Always present on the wire, but
   * typed optional so consumers can render before the data has loaded.
   * Used in the title when {@link isTest} is false.
   */
  runNumber?: number;
  /**
   * Discriminator for the title. Draft processes only ever execute test
   * runs (and we keep "Test run" branding for them). Published runs
   * carry their per-process run number ("Run #1234"). Default
   * (undefined) is treated as a test run so existing callers — and the
   * brief loading window before {@code runNumber} resolves — keep the
   * "Test run" label they had before.
   */
  isTest?: boolean;
  /** Terminal outcome — only read when `phase === 'done'`. */
  runOutcome?: RunOutcome;
  /** Who started the run. Defaults to `'You'`. */
  startedBy?: string;
  /** Runner label. Defaults to `'cloud'`. */
  runnerLabel?: string;
  dataTestId?: string;
}

const fmtClock = (
  startedAt: number | null,
  finishedAt?: number | null,
): string => {
  if (!startedAt) return '00:00';
  const end = finishedAt ?? Date.now();
  const s = Math.max(0, Math.floor((end - startedAt) / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

const NODE_STATUS_TO_STEP: Record<string, StepStatus> = {
  completed: 'succeeded',
  failed: 'failed',
  running: 'inProgress',
  provisioning: 'inProgress',
  waiting_for_runner: 'inProgress',
  waiting_for_user: 'inProgress',
  pending: 'pending',
  skipped: 'skipped',
};

const resolveFromNodeStatus = (
  nodeStatus: ExecutionStep['nodeStatus'],
  phase: RunPhase,
): StepStatus | undefined => {
  if (!nodeStatus) return undefined;
  const mapped = NODE_STATUS_TO_STEP[nodeStatus];
  if (!mapped) return undefined;
  return phase === 'done' && mapped === 'pending' ? 'skipped' : mapped;
};

const phaseToStepStatus = (
  phase: RunPhase,
  nodeStatus: ExecutionStep['nodeStatus'],
  outcome: RunOutcome = 'completed',
): StepStatus => {
  // Per-node SSE state from the engine is the source of truth: a node
  // is "succeeded" only after the engine actually emitted COMPLETED for
  // it. Inferring "succeeded" from topo position used to lie when the
  // engine processed parallel siblings in DB-insertion order rather
  // than topo order -- the unrun sibling earlier in topo order would
  // render as "completed · step N" while the other sibling was still
  // computing.
  const fromNode = resolveFromNodeStatus(nodeStatus, phase);
  // Guard against a stale "running" SSE state surviving into a terminal
  // run -- without this the step would render as "computing" forever
  // when the final per-node COMPLETED/FAILED event was dropped or
  // delayed past the run's own done event.
  if (phase === 'done' && fromNode === 'inProgress') {
    return outcome === 'completed' ? 'succeeded' : 'skipped';
  }
  if (fromNode) return fromNode;

  if (phase === 'done') {
    // Terminal run with no SSE state for this node: on a successful
    // outcome we treat it as succeeded (defensive against dropped SSE
    // events for nodes that did run), on a failed/cancelled outcome we
    // treat it as skipped because the run aborted before reaching it.
    return outcome === 'completed' ? 'succeeded' : 'skipped';
  }
  return 'pending';
};

const formatStepDescription = (
  status: StepStatus,
  stepIndex: number,
  total: number,
  nodeStatus?: string,
): string => {
  if (status === 'succeeded') return `completed · step ${stepIndex + 1}/${total}`;
  if (status === 'inProgress' && nodeStatus === 'provisioning')
    return 'provisioning environment... (this may take a few minutes)';
  if (status === 'inProgress' && nodeStatus === 'waiting_for_user')
    return 'waiting for an assigned user to complete the manual step...';
  if (status === 'inProgress') return `computing · step ${stepIndex + 1}/${total}`;
  if (status === 'failed') return `failed · step ${stepIndex + 1}/${total}`;
  if (status === 'skipped') return `skipped · step ${stepIndex + 1}/${total}`;
  return 'pending';
};

const ExecutionLog: React.FC<ExecutionLogProps> = ({
  phase,
  total,
  order,
  // ``currentId`` is still accepted on the props interface for
  // forward-compat with callers (and for a future "current step" highlight)
  // but is intentionally unused here -- per-step rendering is now driven
  // entirely by the engine's per-node SSE state, see ``phaseToStepStatus``.
  // We destructure it (under an underscore alias) so Sonar's S6767 sees
  // the prop as consumed; the eslint-disable matches the pattern other
  // callers in the repo use for the same "kept-on-interface, ignored
  // in body" shape.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  currentId: _currentId,
  startedAt,
  finishedAt = null,
  runNumber,
  isTest,
  runOutcome = 'completed',
  startedBy = 'You',
  runnerLabel = 'cloud',
  dataTestId = 'execution-log',
}) => {
  const isRunning = phase === 'running';
  const [, tick] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (!isRunning) return;
    const id = globalThis.setInterval(tick, 1000);
    return () => globalThis.clearInterval(id);
  }, [isRunning]);

  const steps: Step[] = useMemo(
    () =>
      order.map((step, i) => {
        const status = phaseToStepStatus(phase, step.nodeStatus, runOutcome);
        return {
          id: step.id,
          title: step.title || step.id,
          status,
          description: formatStepDescription(status, i, total, step.nodeStatus),
        };
      }),
    [order, phase, total, runOutcome],
  );

  if (order.length === 0 && phase !== 'running' && phase !== 'done') {
    return null;
  }

  let statusDotState: 'running' | 'done' | 'failed' = 'failed';
  if (isRunning) statusDotState = 'running';
  else if (runOutcome === 'completed') statusDotState = 'done';

  return (
    <LogCard aria-label="Execution log" data-testid={dataTestId}>
      <LogHead>
        <LogTitleRow>
          <LogStatusDot $state={statusDotState} />
          <LogTitleText>
            {/*
              Discriminator: ``isTest`` is the wire truth for whether
              the user clicked "Test Run" (draft) vs "Run" (published).
              Fall back to the runNumber-presence heuristic for legacy
              callers / the brief loading window before the run dto
              has landed -- in both cases preferring the "Test run"
              label is the safer default.
            */}
            {isTest === false && runNumber
              ? `Run #${runNumber}`
              : 'Test run'}
          </LogTitleText>
        </LogTitleRow>
        <MetaGrid>
          <MetaRow>
            <MetaKey>started by</MetaKey>
            <MetaValue>{startedBy}</MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaKey>started at</MetaKey>
            <MetaValue>
              {startedAt
                ? new Date(startedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : '—'}
            </MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaKey>runner</MetaKey>
            <MetaValue>{runnerLabel}</MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaKey>elapsed</MetaKey>
            <MetaMono>{fmtClock(startedAt, finishedAt)}</MetaMono>
          </MetaRow>
        </MetaGrid>
      </LogHead>

      <LogSep>
        <LogSepLabel>EXECUTION LOG</LogSepLabel>
      </LogSep>

      <StepsScroll data-testid={`${dataTestId}__steps-scroll`}>
        <StepList
          steps={steps}
          density="compact"
          variant="dot"
          ariaLabel="Execution steps"
          dataTestId={`${dataTestId}__steps`}
        />
      </StepsScroll>
    </LogCard>
  );
};

export default ExecutionLog;
